import axios from "axios";
import { getAxiosInstance } from "@lib/utils/axiosInstance";
import type { IAuth } from "./auth";

type TLogFunction = (msg: string) => void;

export default defineNuxtPlugin(async (app) => {
  const { $auth, $logError, $logInfo, $notifyError } = app;
  const { njordaApiBaseUrl, isDevelopment } = useRuntimeConfig().public;

  const logout = function () {
    ($auth as IAuth).logout(true);
    return;
  };

  const $api = getAxiosInstance();
  $api.interceptors.request.use(
    function (config) {
      config.baseURL = `${njordaApiBaseUrl}/mih/v1/`;
      config.withCredentials = true;
      return config;
    },
    function (e) {
      return Promise.reject(e);
    },
  );

  $api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (error && axios.isCancel(error)) {
        if (isDevelopment) {
          ($logInfo as TLogFunction)(">>> API request cancelled");
        }
      } else if (error?.response) {
        const code = parseInt(error.status ?? error.response.status);

        if (code === 401) {
          return logout();
        }
        let errorMsg;
        if (error?.response?.data?.messages) {
          const messages = error.response.data.messages;
          errorMsg = getErrorMessage(messages);
          if (isDevelopment) {
            ($logError as TLogFunction)(`API Error: \n, ${JSON.stringify(messages)}`);
          }
        }
        throw {
          message: errorMsg,
          error: error,
          request: error.request,
          response: error.response,
        };
      } else {
        ($notifyError as Function)({
          text: "Network error. Please try again later.",
        });
      }
      return Promise.reject(error);
    },
  );

  if (($auth as IAuth).loggedIn) {
    await ($auth as IAuth).init().catch(() => {
      logout();
    });
  }

  return {
    provide: {
      api: $api,
    },
  };
});
// TODO: Refine mihApi error handling:
// - Standardize the error object shape returned to callers (e.g. { code, message, details }).
// - Map backend `messages` and HTTP status codes to user-friendly, localized notifications.
// - Ensure all 401/403 responses consistently trigger logout or token refresh as per auth policy.
// - Centralize logging of unexpected errors (5xx, parsing issues, unknown shapes) via $logError.
// - Evaluate adding retry/backoff for transient network errors and timeouts (non-cancelled cases).
